import React from "react";
import {
  Img,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { FLOAT_SHADOW, LOOK } from "../look";

/** Springy 0→1 from local frame `at` (snappy, slight overshoot). */
export const usePop = (at: number, soft = false) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  return spring({
    frame: frame - at,
    fps,
    config: soft
      ? { damping: 18, stiffness: 170, mass: 0.8 }
      : { damping: 13, stiffness: 230, mass: 0.6 },
  });
};

/** Floating white card: the base of every visual. */
export const Card: React.FC<{
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ children, style }) => (
  <div
    style={{
      position: "relative",
      borderRadius: 44,
      background: LOOK.white,
      boxShadow: FLOAT_SHADOW,
      overflow: "hidden",
      ...style,
    }}
  >
    {children}
  </div>
);

/**
 * Browser / dashboard window, like the partner page's feature cards:
 * a Frak-blue title bar with the three dots, and either a URL pill (a
 * brand's site) or nothing (the Frak dashboard).
 */
export const Window: React.FC<{
  children: React.ReactNode;
  url?: string;
  /** Pill at the right of the title bar ("Exemple": sample figures). */
  tag?: string;
  style?: React.CSSProperties;
  bodyStyle?: React.CSSProperties;
}> = ({ children, url, tag, style, bodyStyle }) => (
  <Card style={{ display: "flex", flexDirection: "column", ...style }}>
    <div
      style={{
        height: 64,
        flex: "none",
        background: LOOK.chrome,
        display: "flex",
        alignItems: "center",
        padding: "0 26px",
        gap: 12,
      }}
    >
      {["#FF5F57", "#FEBC2E", "#28C840"].map((c) => (
        <div
          key={c}
          style={{ width: 16, height: 16, borderRadius: 16, background: c }}
        />
      ))}
      {url ? (
        <div
          style={{
            marginLeft: 22,
            flex: 1,
            maxWidth: 520,
            height: 38,
            borderRadius: 38,
            background: "rgba(255,255,255,0.18)",
            color: LOOK.white,
            fontFamily: LOOK.body,
            fontWeight: LOOK.medium,
            fontSize: 22,
            display: "flex",
            alignItems: "center",
            padding: "0 22px",
            gap: 10,
          }}
        >
          <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
            <rect
              x="1.5"
              y="7.5"
              width="13"
              height="9.5"
              rx="2.5"
              fill={LOOK.white}
            />
            <path
              d="M4.5 8V5.5a3.5 3.5 0 0 1 7 0V8"
              stroke={LOOK.white}
              strokeWidth="2"
            />
          </svg>
          {url}
        </div>
      ) : null}
      {tag ? (
        <div
          style={{
            marginLeft: "auto",
            padding: "6px 18px",
            borderRadius: 999,
            background: "rgba(255,255,255,0.2)",
            color: LOOK.white,
            fontFamily: LOOK.body,
            fontWeight: LOOK.semi,
            fontSize: 24,
          }}
        >
          {tag}
        </div>
      ) : null}
    </div>
    <div style={{ position: "relative", flex: 1, ...bodyStyle }}>
      {children}
    </div>
  </Card>
);

/** Round avatar from `public/`, or an initial on a tint. */
export const Avatar: React.FC<{
  size: number;
  src?: string;
  initial?: string;
  tint?: string;
  ring?: number;
}> = ({ size, src, initial, tint = LOOK.blue, ring = 0 }) => (
  <div
    style={{
      width: size,
      height: size,
      flex: "none",
      borderRadius: size,
      overflow: "hidden",
      background: tint,
      border: ring ? `${ring}px solid ${LOOK.white}` : undefined,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: LOOK.white,
      fontFamily: LOOK.display,
      fontWeight: LOOK.bold,
      fontSize: size * 0.42,
    }}
  >
    {src ? (
      <Img
        src={staticFile(src)}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          scale: "1.08",
        }}
      />
    ) : (
      initial
    )}
  </div>
);
